"use client"

import React from "react"

export function MultiMetricLineChart({
  series,
  title,
  height = 180,
  xLabel = "Iteration",
  yLabel = "Value",
  yFmt = (v) => v.toFixed(2),
}) {
  if (!series || !series.length) return null

  const visibleWidth = 520

  const allPoints = series.flatMap((s) => s.points || [])
  if (!allPoints.length) return null

  const xs = allPoints.map((p) => Number(p.iteration ?? 0))
  const ys = allPoints.map((p) => Number(p.value ?? 0))

  const minX = Math.min(...xs)
  const maxX = Math.max(...xs)

  // Y range με λίγο “αέρα” πάνω
  const rawMinY = Math.min(...ys)
  const rawMaxY = Math.max(...ys)

  let minY = rawMinY
  let maxY = rawMaxY

  if (!isFinite(minY) || !isFinite(maxY)) return null

  if (maxY === minY) {
    // αν όλα τα values είναι ίδια, άνοιξε λίγο το εύρος
    const delta = Math.abs(maxY || 1) * 0.1 || 1
    minY = maxY - delta
    maxY = maxY + delta
  } else {
    const span = maxY - minY
    maxY = maxY + span * 0.1 // +10% headroom
  }

  const contentWidth = Math.max(visibleWidth, allPoints.length * 50)
  const pad = { top: 40, right: 20, bottom: 36, left: 50 }
  const innerW = contentWidth - pad.left - pad.right
  const innerH = height - pad.top - pad.bottom

  const scaleX = (x) =>
    pad.left + (innerW * (x - minX)) / Math.max(1e-9, maxX - minX || 1)
  const scaleY = (y) =>
    pad.top +
    innerH -
    (innerH * (y - minY)) / Math.max(1e-9, maxY - minY || 1)

  const ticksY = []
  const steps = 4
  for (let i = 0; i <= steps; i++) {
    const v = minY + (i * (maxY - minY || 1)) / steps
    ticksY.push(v)
  }

  const uniqueIterations = Array.from(new Set(xs)).sort((a, b) => a - b)
  const xStep = Math.max(1, Math.floor(uniqueIterations.length / 8))

  return (
    <div className="border rounded-md p-2 bg-white dark:bg-gray-900 dark:border-gray-700">
      {title && (
        <div className="text-sm font-medium mb-1 text-gray-900 dark:text-gray-100">
          {title}
        </div>
      )}

      <div
        className="w-full overflow-x-auto overflow-y-hidden"
        style={{ cursor: "grab" }}
      >
        <svg width={contentWidth} height={height} className="select-none">
          <rect
            x={0}
            y={0}
            width={contentWidth}
            height={height}
            fill="transparent"
          />

          {/* horizontal grid + Y labels */}
          {ticksY.map((v, idx) => {
            const y = scaleY(v)
            return (
              <g key={idx}>
                <line
                  x1={pad.left}
                  y1={y}
                  x2={contentWidth - pad.right}
                  y2={y}
                  stroke="currentColor"
                  className="text-gray-100 dark:text-gray-800"
                  strokeWidth={1}
                />
                <text
                  x={pad.left - 6}
                  y={y + 4}
                  textAnchor="end"
                  fontSize={10}
                  className="fill-gray-500 dark:fill-gray-400"
                >
                  {yFmt(v)}
                </text>
              </g>
            )
          })}

          {/* axes */}
          <line
            x1={pad.left}
            y1={pad.top}
            x2={pad.left}
            y2={pad.top + innerH}
            stroke="currentColor"
            className="text-gray-300 dark:text-gray-700"
            strokeWidth={1.2}
          />
          <line
            x1={pad.left}
            y1={pad.top + innerH}
            x2={pad.left + innerW}
            y2={pad.top + innerH}
            stroke="currentColor"
            className="text-gray-300 dark:text-gray-700"
            strokeWidth={1.2}
          />

          {/* X ticks */}
          {uniqueIterations.map((xVal, idx) => {
            if (idx % xStep !== 0) return null
            const x = scaleX(xVal)
            const yAxis = pad.top + innerH
            return (
              <g key={xVal}>
                <line
                  x1={x}
                  y1={yAxis}
                  x2={x}
                  y2={yAxis + 4}
                  stroke="currentColor"
                  className="text-gray-400 dark:text-gray-500"
                  strokeWidth={1}
                />
                <text
                  x={x}
                  y={yAxis + 14}
                  textAnchor="middle"
                  fontSize={10}
                  className="fill-gray-500 dark:fill-gray-400"
                >
                  {xVal}
                </text>
              </g>
            )
          })}

          {/* axis labels */}
          <text
            x={pad.left + innerW / 2}
            y={height - 4}
            textAnchor="middle"
            fontSize={11}
            className="fill-gray-600 dark:fill-gray-300"
          >
            {xLabel}
          </text>
          <text
            x={12}
            y={pad.top + innerH / 2}
            textAnchor="middle"
            fontSize={11}
            transform={`rotate(-90 12 ${pad.top + innerH / 2})`}
            className="fill-gray-600 dark:fill-gray-300"
          >
            {yLabel}
          </text>

          {/* series */}
          {series.map((s) => {
            const pts = (s.points || []).sort(
              (a, b) => (a.iteration ?? 0) - (b.iteration ?? 0),
            )
            if (!pts.length) return null

            const path = pts
              .map((d, i) => {
                const x = scaleX(Number(d.iteration ?? i + 1))
                const y = scaleY(Number(d.value ?? 0))
                return `${i === 0 ? "M" : "L"} ${x} ${y}`
              })
              .join(" ")

            return (
              <g key={s.id || s.label}>
                <path
                  d={path}
                  fill="none"
                  stroke={s.color || "#10b981"}
                  strokeWidth={2}
                />
                {pts.map((d, idx) => {
                  const xVal = Number(d.iteration ?? idx + 1)
                  const yVal = Number(d.value ?? 0)
                  const x = scaleX(xVal)
                  const y = scaleY(yVal)
                  return (
                    <g key={`${s.id || s.label}-${idx}`}>
                      <circle
                        cx={x}
                        cy={y}
                        r={3}
                        fill={s.color || "#10b981"}
                      />
                      <text
                        x={x}
                        y={y - 6}
                        textAnchor="middle"
                        fontSize={9}
                        className="fill-gray-700 dark:fill-gray-200"
                      >
                        {yFmt(yVal)}
                      </text>
                    </g>
                  )
                })}
              </g>
            )
          })}

          {/* legend */}
          <g transform={`translate(${pad.left + 10}, ${pad.top - 18})`}>
            {series.map((s, idx) => (
              <g key={s.id || s.label} transform={`translate(${idx * 120}, 0)`}>
                <rect
                  x={0}
                  y={-8}
                  width={10}
                  height={10}
                  fill={s.color || "#10b981"}
                />
                <text
                  x={16}
                  y={0}
                  textAnchor="start"
                  fontSize={11}
                  className="fill-gray-700 dark:fill-gray-200"
                >
                  {s.label}
                </text>
              </g>
            ))}
          </g>
        </svg>
      </div>
    </div>
  )
}

export function ScrollableBarChartWithAxes({
  data,
  title,
  height = 180,
  yFmt = (v) => `${Math.round(v)} ms`,
  xLabel = "Iteration",
  yLabel = "Elapsed time",
}) {
  if (!data || data.length === 0) return null

  const visibleWidth = 520
  const contentWidth = Math.max(visibleWidth, data.length * 50)

  const xs = data.map((d) => Number(d.iteration ?? 0))
  const ys = data.map((d) => Number(d.ms ?? 0))

  // μοναδικές τιμές iteration ως κατηγορίες
  const uniqueIterations = Array.from(new Set(xs)).sort((a, b) => a - b)
  const nCats = uniqueIterations.length || 1

  const idxByIter = new Map()
  uniqueIterations.forEach((val, idx) => idxByIter.set(val, idx))

  // Y range με λίγο headroom
  const minY = 0
  const rawMaxY = Math.max(...ys, 1)
  const maxY = rawMaxY * 1.15

  const pad = { top: 40, right: 24, bottom: 36, left: 70 }
  const innerW = contentWidth - pad.left - pad.right
  const innerH = height - pad.top - pad.bottom

  // scaleX τώρα δουλεύει σε index (0..nCats-1), όχι σε πραγματικές τιμές
  const scaleXCenter = (iter) => {
    const idx = idxByIter.get(iter) ?? 0
    // κέντρο στο μέσο του κάθε “slot”
    return pad.left + innerW * ((idx + 0.5) / nCats)
  }

  const scaleY = (y) =>
    pad.top +
    innerH -
    (innerH * (y - minY)) / Math.max(1e-9, maxY - minY || 1)

  const barWidth = (innerW / nCats) * 0.6

  const ticksY = []
  const steps = 4
  for (let i = 0; i <= steps; i++) {
    const v = minY + (i * (maxY - minY || 1)) / steps
    ticksY.push(v)
  }

  const xStep = Math.max(1, Math.floor(nCats / 8))

  return (
    <div className="border rounded-md p-2 bg-white dark:bg-gray-900 dark:border-gray-700">
      {title && (
        <div className="text-sm font-medium mb-1 text-gray-900 dark:text-gray-100">
          {title}
        </div>
      )}
      <div
        className="w-full overflow-x-auto overflow-y-hidden"
        style={{ cursor: "grab" }}
      >
        <svg width={contentWidth} height={height} className="select-none">
          <rect
            x={0}
            y={0}
            width={contentWidth}
            height={height}
            fill="transparent"
          />

          {/* horizontal grid + Y labels */}
          {ticksY.map((v, idx) => {
            const y = scaleY(v)
            return (
              <g key={idx}>
                <line
                  x1={pad.left}
                  y1={y}
                  x2={contentWidth - pad.right}
                  y2={y}
                  stroke="currentColor"
                  className="text-gray-100 dark:text-gray-800"
                  strokeWidth={1}
                />
                <text
                  x={pad.left - 8}
                  y={y + 4}
                  textAnchor="end"
                  fontSize={10}
                  className="fill-gray-500 dark:fill-gray-400"
                >
                  {yFmt(v)}
                </text>
              </g>
            )
          })}

          {/* axes */}
          <line
            x1={pad.left}
            y1={pad.top}
            x2={pad.left}
            y2={pad.top + innerH}
            stroke="currentColor"
            className="text-gray-300 dark:text-gray-700"
            strokeWidth={1.2}
          />
          <line
            x1={pad.left}
            y1={pad.top + innerH}
            x2={pad.left + innerW}
            y2={pad.top + innerH}
            stroke="currentColor"
            className="text-gray-300 dark:text-gray-700"
            strokeWidth={1.2}
          />

          {/* X ticks κάτω από τα κέντρα των bars */}
          {uniqueIterations.map((iter, idx) => {
            if (idx % xStep !== 0) return null
            const xCenter = scaleXCenter(iter)
            const yAxis = pad.top + innerH
            return (
              <g key={iter}>
                <line
                  x1={xCenter}
                  y1={yAxis}
                  x2={xCenter}
                  y2={yAxis + 4}
                  stroke="currentColor"
                  className="text-gray-400 dark:text-gray-500"
                  strokeWidth={1}
                />
                <text
                  x={xCenter}
                  y={yAxis + 14}
                  textAnchor="middle"
                  fontSize={10}
                  className="fill-gray-500 dark:fill-gray-400"
                >
                  {iter}
                </text>
              </g>
            )
          })}

          {/* axis labels */}
          <text
            x={pad.left + innerW / 2}
            y={height - 4}
            textAnchor="middle"
            fontSize={11}
            className="fill-gray-600 dark:fill-gray-300"
          >
            {xLabel}
          </text>
          <text
            x={24}
            y={pad.top + innerH / 2}
            textAnchor="middle"
            fontSize={11}
            transform={`rotate(-90 24 ${pad.top + innerH / 2})`}
            className="fill-gray-600 dark:fill-gray-300"
          >
            {yLabel}
          </text>

          {/* bars + labels */}
          {data.map((d, idx) => {
            const iter = Number(d.iteration ?? 0)
            const yVal = Number(d.ms ?? 0)
            const xCenter = scaleXCenter(iter)
            const x = xCenter - barWidth / 2
            const y = scaleY(yVal)
            const baseY = pad.top + innerH
            const hBar = baseY - y
            return (
              <g key={idx}>
                <rect
                  x={x}
                  y={y}
                  width={barWidth}
                  height={hBar}
                  className="fill-blue-500/70 dark:fill-blue-400/80"
                />
                <text
                  x={xCenter}
                  y={y - 4}
                  textAnchor="middle"
                  fontSize={9}
                  className="fill-gray-700 dark:fill-gray-200"
                >
                  {yFmt(yVal)}
                </text>
              </g>
            )
          })}
        </svg>
      </div>
    </div>
  )
}
